import { useState, useEffect, useContext } from "react";
import {
  CustomButton,
  RegularButton,
} from "../../../common/ReusableComponent/CustomButton";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput } from "../../../common/ReusableComponent/ReusableInput";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../common/ToastHandler";
import useSwal from "../../../common/ReusableComponent/useSwal";
import { capitalizeFirstLetter } from "../../../../utils/helper/common";
import AssetTable from "../AssetsInventory/AssetTable"
import AddCategoryIcon from "../../../../assets/Image/categories.png"
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import useMyPermissions from "../../../../utils/permissions/useMyPermissions";
import Can from "../../../../utils/permissions/Can";

const AssetsCategory = () => {

  const [showModal, setShowModal] = useState(false);
  const { user } = useContext(AuthContext);
  const { can } = useMyPermissions();
  const storeId = user.store;
  const { confirmDelete } = useSwal();
  const { handleToast } = useToastHandler();
  const { GetPaginatedData, Post, Delete, Put } = useApi();
  const [loading, setLoading] = useState(false);
  const [categoriesData, setCategoriesData] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    category_name: "",
    description: "",
    is_active: false,
  });

  const [errors, setErrors] = useState({
    category_name: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    if (name === "category_name" && type !== "checkbox") {
      processedValue = capitalizeFirstLetter(value);
    }

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : processedValue,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";

    if (name === "category_name") {
      if (!value.trim()) {
        error = "Category name is required";
      } else if (value.length < 2) {
        error = "Category name must be at least 2 characters";
      } else if (value.length > 50) {
        error = "Category name must not exceed 50 characters";
      }
    }

    if (name === "description" && value && value.length > 200) {
      error = "Description must not exceed 200 characters";
    }

    setErrors({
      ...errors,
      [name]: error,
    });
  };

  const validateForm = () => {
    if (!formData.category_name.trim()) {
      setErrors(prev => ({ ...prev, category_name: "Category name is required" }));
      return false;
    }
    if (formData.category_name.length < 2) {
      setErrors(prev => ({ ...prev, category_name: "Category name must be at least 2 characters" }));
      return false;
    }
    if (formData.category_name.length > 50) {
      setErrors(prev => ({ ...prev, category_name: "Category name must not exceed 50 characters" }));
      return false;
    }
    if (formData.description && formData.description.length > 200) {
      setErrors(prev => ({ ...prev, description: "Description must not exceed 200 characters" }));
      return false;
    }
    return true;
  };

  const fetchCategoryList = async ({ page = 1, searchTerm = "", } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData("category", {
        page,
        search: searchTerm || undefined,

      });

      const results = response?.data || [];
      setCategoriesData(results);

      setTotalCount(response?.totalCount || 0);

      const totalCount = response?.totalCount || 0;
      const totalPages = Math.ceil(totalCount / 10);
      return {
        data: results.map(item => ({
          ...item,
          category_name: item.category_name || "Not specified",
          description: item.description || "No description available",
          status_display: (
            <div
              style={{
                padding: "5px 10px",
                borderRadius: "15px",
                display: "inline-block",
                backgroundColor: item.is_active ? "#28a745" : "#dc3545",
                color: "#fff",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {item.is_active ? "Active" : "Inactive"}
            </div>
          )
        })),
        totalPages,
        totalCount,
        currentPage: page
      };

    } catch (error) {
      console.error("Fetch error:", error);
      setCategoriesData([]);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch categories",
      });

      return { data: [], totalPages: 1, totalCount: 0, currentPage: page };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchCategoryList({ page: currentPage, searchTerm: debouncedSearchTerm });
  }, [currentPage, debouncedSearchTerm, refreshTrigger]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      category_name: formData.category_name.trim(),
      description: formData.description.trim(),
      store: storeId,
      is_active: formData.is_active,
    };

    const apiCall = editingCategory
      ? Put("category", editingCategory.id, payload)
      : Post("category", payload);

    try {
      await apiCall;
      handleToast({
        type: "SUCCESS",
        message: `Category ${editingCategory ? "updated" : "added"} successfully`,
      });
      setShowModal(false);
      resetForm();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: `Failed to ${editingCategory ? "update" : "add"} category`,
      });
    }
  };

  const handleEditCategory = (categoryIdOrObject) => {
    console.log("Edit received:", categoryIdOrObject);
    console.log("Categories data:", categoriesData);

    let categoryObject;

    if (typeof categoryIdOrObject === 'string') {
      categoryObject = categoriesData.find(item => item.id === categoryIdOrObject);
      console.log("Found category:", categoryObject);
    } else if (categoryIdOrObject && typeof categoryIdOrObject === 'object') {
      categoryObject = categoryIdOrObject;
    }

    if (!categoryObject || !categoryObject.id) {
      console.error("Invalid category object or missing ID:", categoryObject);
      handleToast({
        type: "ERROR",
        message: "Cannot edit: Invalid item selected"
      });
      return;
    }

    setEditingCategory({ ...categoryObject });
    setFormData({
      category_name: categoryObject.category_name || "",
      description: categoryObject.description || "",
      is_active: categoryObject.is_active ?? true,
    });
    setShowModal(true);
  };

  const handleDeleteCategory = async (categoryIdOrObject) => {
    const id = typeof categoryIdOrObject === 'string' ? categoryIdOrObject : categoryIdOrObject?.id;

    if (!id) {
      handleToast({
        type: "ERROR",
        message: "Cannot delete: Invalid item selected"
      });
      return;
    }

    const result = await confirmDelete(
      "Are you sure?",
      "You won't be able to revert this!"
    );

    if (!result.isConfirmed) return;

    try {
      await Delete("category", id);
      handleToast({
        type: "SUCCESS",
        message: "Category deleted successfully",
      });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: "Failed to delete category",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      category_name: "",
      description: "",
      is_active: false,
    });
    setErrors({
      category_name: "",
      description: "",
    });
    setEditingCategory(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const CATEGORY_STATUS_PILL = {
  active: {
    bg: "#e9fdf3",
    text: "#12b76a",
    border: "#a6f4c5",
  },
  inactive: {
    bg: "#fdecec",
    text: "#e11d48",
    border: "#fda4af",
  },
};

  const columns = [
    { header: "Category Name", field: "category_name" },
    { header: "Description", field: "description" },
    {
    header: "Status",
    render: (row) => {
      const status = row.is_active ? "active" : "inactive";
      const cfg = CATEGORY_STATUS_PILL[status];

      return (
        <StatusPill
          label={status}
          bgColor={cfg.bg}
          textColor={cfg.text}
          borderColor={cfg.border}
        />
      );
    },
  },
  ];


  const transformedData = categoriesData.map(item => ({
    ...item,
    category_name: item.category_name || "Not specified",
    description: item.description || "No description available",
    status_display: (
      <div
        style={{
          display: "inline-block",
          width: "170px",
          textAlign: "center",
          padding: "6px 0",
          borderRadius: "4px",
          fontSize: "16px",
          backgroundColor: item.is_active ? "#198F51" : "#FF2024",
          color: "#fff",
          fontWeight: "500",
        }}
      >
        {item.is_active ? "Active" : "Inactive"}
      </div>
    )
  }));

  return (
    <>
      <div className="responsive-box">
        <div className="inventory-header">
          <div className="inventory-title">Assets Category</div>
          <Can module="assetService" action="add">
            <CustomButton
              text="Add Category"
              icon={AddCategoryIcon}
              onClick={() => setShowModal(true)}
              color="#000000"
              backgroundColor="#FFAF32"
              iconPosition="right"
            />
          </Can>
        </div>

        <AssetTable
          data={transformedData}
          columns={columns}
          loading={loading}
          showAction={true}
          onEdit={can("assetService", "change") ? handleEditCategory : undefined}
          onDelete={can("assetService", "delete") ? handleDeleteCategory : undefined}
          showFooter
          currentPage={currentPage}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          searchTerm={searchTerm}
          onSearch={handleSearch}
        />

        <ReusableModal
          title={`${editingCategory ? "Edit" : "Add"} Category`}
          show={showModal}
          handleClose={handleCloseModal}
          width="450px"
          height="350px"
        >
          <form onSubmit={handleAddCategory}>
            <div className="d-flex flex-column mx-3">
              <ReusableInput
                type="text"
                name="category_name"
                label="Category Name"
                value={formData.category_name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                maxLength={50}
                error={errors.category_name}
              />
              <div className="d-flex flex-column mt-4">
                <ReusableInput
                  type="textarea"
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={3}
                  maxLength={200}
                  error={errors.description}
                />
              </div>
              <div className="d-flex gap-3 mx-3">

                <input
                  type="checkbox"
                  name="is_active"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  style={{
                    transform: "scale(1.5)",
                    accentColor: "black",
                  }}
                />
                <label className="form-check-label" htmlFor="is_active">
                  Active
                </label>
              </div>
              <div className="d-flex justify-content-end mt-3">
                {!editingCategory && (
                  <RegularButton
                    text="Clear"
                    onClick={resetForm}
                    backgroundColor="#ffffff"
                    color="#282E26"
                  />
                )}
                <RegularButton
                  text={editingCategory ? "Update" : "Save"}
                  type="submit"
                  backgroundColor="#282e26"
                />
              </div>
            </div>
          </form>
        </ReusableModal>
      </div>
    </>
  );
};

export default AssetsCategory;
